var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import api from '@utils/axios';
import { REWORD_VERDICT } from '../types';
const DOCUMENTS_PATH = '/api/admin/tkb/documents';
const documentPath = (id) => `${DOCUMENTS_PATH}/${id}`;
const documentContentPath = (id) => `${DOCUMENTS_PATH}/${id}/content`;
const documentChildRuleStatsPath = (id) => `${DOCUMENTS_PATH}/${id}/child_rule_stats`;
const RULES_PATH = '/api/admin/tkb/rules';
const rulePath = (id) => `${RULES_PATH}/${id}`;
const ruleVersionsPath = (id) => `${RULES_PATH}/${id}/versions`;
const ruleRollbackPath = (id) => `${RULES_PATH}/${id}/rollback`;
const CHECK_CONFIGS_PATH = '/api/admin/tkb/ia_check_configs';
const checkConfigPath = (id) => `${CHECK_CONFIGS_PATH}/${id}`;
const PREVIEW_PATH = '/api/admin/tkb/ia_config/preview';
// Documents
export function listDocuments() {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield api.get(DOCUMENTS_PATH, {
            headers: { Accept: 'application/json' }
        });
        return response.data;
    });
}
export function getDocumentContent(id) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield api.get(documentContentPath(id), {
            headers: { Accept: 'application/json' }
        });
        return response.data;
    });
}
export function getDocChildRuleStats(id) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield api.get(documentChildRuleStatsPath(id), {
            headers: { Accept: 'application/json' }
        });
        return response.data;
    });
}
export function updateDocument(id, payload) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield api.patch(documentPath(id), payload, {
            headers: { Accept: 'application/json' }
        });
        return response.data;
    });
}
export function deleteDocument(id) {
    return __awaiter(this, void 0, void 0, function* () {
        yield api.delete(documentPath(id), { headers: { Accept: 'application/json' } });
    });
}
// Rules
export function listRules() {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield api.get(RULES_PATH, {
            headers: { Accept: 'application/json' }
        });
        return response.data;
    });
}
// The manual-entry safety gate (nl-extraction-output-v1) screens the directive
// before persisting. A 200/201 carrying `verdict: 'reword'` is NOT a saved rule
// — it's a salvaged proposal (nothing persisted). A reject surfaces as a thrown
// 422 with the reason in response.data.error (handled by the caller's onError).
function toSaveResult(data) {
    if (data.verdict === REWORD_VERDICT) {
        return { kind: 'reword', proposal: data };
    }
    return { kind: 'saved', rule: data };
}
export function createRule(payload) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield api.post(RULES_PATH, { rule: payload }, {
            headers: { Accept: 'application/json' }
        });
        return toSaveResult(response.data);
    });
}
export function updateRule(id, payload) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield api.patch(rulePath(id), { rule: payload }, {
            headers: { Accept: 'application/json' }
        });
        return toSaveResult(response.data);
    });
}
export function deleteRule(id) {
    return __awaiter(this, void 0, void 0, function* () {
        yield api.delete(rulePath(id), { headers: { Accept: 'application/json' } });
    });
}
// Rule versions + rollback (task-04)
export function listRuleVersions(id) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield api.get(ruleVersionsPath(id), {
            headers: { Accept: 'application/json' }
        });
        return response.data;
    });
}
export function rollbackRule(id, toVersion) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield api.post(ruleRollbackPath(id), { to_version: toVersion }, { headers: { Accept: 'application/json' } });
        return response.data;
    });
}
// Check configs (task-11 Checks tab)
export function listCheckConfigs() {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield api.get(CHECK_CONFIGS_PATH, {
            headers: { Accept: 'application/json' }
        });
        return response.data;
    });
}
export function createCheckConfig(payload) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield api.post(CHECK_CONFIGS_PATH, { ia_check_config: payload }, { headers: { Accept: 'application/json' } });
        return response.data;
    });
}
export function updateCheckConfig(id, payload) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield api.patch(checkConfigPath(id), { ia_check_config: payload }, { headers: { Accept: 'application/json' } });
        return response.data;
    });
}
export function deleteCheckConfig(id) {
    return __awaiter(this, void 0, void 0, function* () {
        yield api.delete(checkConfigPath(id), { headers: { Accept: 'application/json' } });
    });
}
// Buildings search — lightweight name+address autocomplete for scope pickers (UX rebuild)
const BUILDINGS_SEARCH_PATH = '/api/admin/tkb/buildings/search';
export function searchBuildings(query_1) {
    return __awaiter(this, arguments, void 0, function* (query, limit = 30) {
        const response = yield api.get(BUILDINGS_SEARCH_PATH, {
            params: { q: query, limit },
            headers: { Accept: 'application/json' }
        });
        return response.data.buildings;
    });
}
// Accounts search — lightweight name autocomplete for scope pickers (UX rebuild)
const ACCOUNTS_SEARCH_PATH = '/api/admin/tkb/accounts/search';
export function searchAccounts(query_1) {
    return __awaiter(this, arguments, void 0, function* (query, limit = 30) {
        const response = yield api.get(ACCOUNTS_SEARCH_PATH, {
            params: { q: query, limit },
            headers: { Accept: 'application/json' }
        });
        return response.data.accounts;
    });
}
// Technicians search — actor picker for ipa-technician-scoped-rules
const TECHNICIANS_SEARCH_PATH = '/api/admin/tkb/technicians/search';
export function searchTechnicians(query_1) {
    return __awaiter(this, arguments, void 0, function* (query, limit = 30) {
        const response = yield api.get(TECHNICIANS_SEARCH_PATH, {
            params: { q: query, limit },
            headers: { Accept: 'application/json' }
        });
        return response.data.technicians;
    });
}
// Teams search — actor picker for ipa-technician-scoped-rules
const TEAMS_SEARCH_PATH = '/api/admin/tkb/teams/search';
export function searchTeams(query_1) {
    return __awaiter(this, arguments, void 0, function* (query, limit = 30) {
        const response = yield api.get(TEAMS_SEARCH_PATH, {
            params: { q: query, limit },
            headers: { Accept: 'application/json' }
        });
        return response.data.teams;
    });
}
export function previewResolvedConfig(params) {
    return __awaiter(this, void 0, void 0, function* () {
        const { buildingId, technicianId, appliesTo = 'ia' } = params;
        const response = yield api.get(PREVIEW_PATH, {
            params: Object.assign(Object.assign(Object.assign({}, (buildingId != null ? { building_id: buildingId } : {})), (technicianId != null ? { technician_id: technicianId } : {})), { applies_to: appliesTo }),
            headers: { Accept: 'application/json' }
        });
        return response.data;
    });
}
